import { HelperService } from "../helper/helper";
import { DeviceManager, DeviceProtocol } from "../device";
import { DialogType, enableDialog } from "@/components/cdialog/dialog";
import { loadingContent } from "@/components/loading/loading.vue";
import { isElectron } from '@libs/utils/envUtils';
import { DriverSocketMsgType } from "@libs/types/message";
import { magnetSet } from "../driver/magnet_set";
import i18n from '@/languages/i18n';
import { baseSet } from "../driver/base_set";
import { CurrentDevice, DeviceChange, DeviceChangeType, WatchDevice } from "@libs/types/response";
import { deviceZone, IKeyZone } from "../device_zone";
import { powerSet } from "../driver/power_set";
import { calibrationHandler } from "../driver/calibration_set";
import { DevicePid } from "@/define/define";
import { driverServiceVersion, VersionStatus } from "@/upgrade/service";
import { SocketService } from "@libs/socket/socket_service";
import { errorMessages } from "../error_messages";
const { t } = i18n.global;

export class EXEDriver {
  socketService: SocketService = new SocketService({
    autoReconnect: true,
    reconnectAttempts: Infinity,  // Infinity 无限重连
    reconnectDelay: 1000,         // 1s重连一次
  })
  private retryCount: number = 0;
  private launchServiceInterval: NodeJS.Timeout | null = null;
  constructor() {
    if (import.meta.env.PROD) {
      if (!isElectron()) return
    }
    this.initSocketConnection()
  }

  restartService() {
    if (this.launchServiceInterval != null) {
      clearInterval(this.launchServiceInterval);
    }
    this.launchServiceInterval = setInterval(() => {
      if (this.retryCount < 5) {
        this.retryCount++;
        HelperService.instance.launchDriverService().catch(_ => {
          if (this.retryCount == 5) {
            enableDialog(t('start_driver_service_fail'), DialogType.waiting, false)
          }
        })
      } else {
        this.clearLaunchServiceInterval()
      }
    }, 1000)
  }

  // 销毁启动驱动服务的定时器
  private clearLaunchServiceInterval() {
    if (this.launchServiceInterval) {
      clearInterval(this.launchServiceInterval);
      this.launchServiceInterval = null;
      this.retryCount = 0;
    }
  }

  private initSocketConnection() {
    this.socketService.connect(new WebSocket("ws://127.0.0.1:8083/entry"));
    this.socketService.setMsgErrorCallback(this.showErrorNotification)
    this.socketService.setEventHandlers({
      onOpen: (_event) => this.handleOpen(),
      onClose: (_event) => this.onDriverSocketClose(),
      onError: (event) => console.error('Error occurred:', event)
    });
  }
  private showErrorNotification(errMessage: string) {
    const errMsg = errorMessages[errMessage] ? t(errorMessages[errMessage].i18n) : errMessage
    enableDialog(errMsg, DialogType.notice, false)
  }

  private handleOpen() {
    enableDialog("", DialogType.notice)
    this.clearLaunchServiceInterval();
    this.askDefineZones();
    this.askDevices()
    this.askDriverServiceVersion()
    this.handleNotificationMsgs()
  }

  private onDriverSocketClose() {
    // 如果不是在electron环境下，则不尝试重连
    if (isElectron() == false) {
      return;
    }
    enableDialog(t('reloading_driver'), DialogType.waiting)
    this.clearDataOnReconnect()
    if (driverServiceVersion.serviceUpdateVersionStatus === VersionStatus.NoUpdate) {
      this.restartService()
    }
  }

  //重连清空数据
  private clearDataOnReconnect() {
    this._serviceVersion = null; //服务版本
    DeviceManager.instance.resetState()
    calibrationHandler.resetCalibrationStatus()

  }

  private askDefineZones() {
    this.socketService.askType<IKeyZone>(DriverSocketMsgType.MTGetKeyZones).then((res) => {
      deviceZone.askDefineZones(res, DeviceProtocol.dll_exe)
    })
  }


  private async askDevices(device: WatchDevice | null = null) {
    loadingContent.value = t('loading_devices')
    if (device != null && device.In == true && device.Device == DevicePid.IYXAmber358_24g8k) {
      loadingContent.value = t('loading_amber_device')
    }
    const devices = await this.socketService.askType<CurrentDevice[]>(DriverSocketMsgType.MTCurrentDevices)
    loadingContent.value = ""
    if (!devices) {
      return;
    }
    DeviceManager.instance.state.deviceList = devices
  }

  private _serviceVersion: string | null = null;
  private onReceivedServiceVersionCallbacks: ((version: string) => void)[] = [];

  // 获取service 版本并缓存，如果已经获取过则直接返回
  public async getServiceVersion(): Promise<string> {
    if (this._serviceVersion) {
      return this._serviceVersion;
    }
    return new Promise((resolve) => {
      // 如果版本信息还没有获取，则推送到回调队列
      this.onReceivedServiceVersionCallbacks.push(resolve);
    });
  }

  private async askDriverServiceVersion() {
    const version = await this.socketService.askType<string>(DriverSocketMsgType.MTVersionSVC);
    DeviceManager.instance.state.driverServiceVersion = version
    this._serviceVersion = version;
    this.onReceivedServiceVersionCallbacks.forEach((callback) => callback(version));
  }

  private handleNotificationMsgs() {
    this.socketService.setHandler(DriverSocketMsgType.MSWatchDevice, (res: WatchDevice) => {
      this.changeDevice(res)
      magnetSet.clearMagnetSet(res)
    }, (err: string) => {
      console.error("MSWatchDevice:", err)
    })
    this.socketService.setHandler(DriverSocketMsgType.MSDeviceChange, (res: DeviceChange) => {
      switch (res.ChangeType) {
        case DeviceChangeType.dpi:
          baseSet.changeMouseDpi(res.ChangeData.DpiIndex)
          break;
        case DeviceChangeType.power:
          powerSet.getPowerData()
          break;
        case DeviceChangeType.status:
          DeviceManager.instance.state.deviceStatus = res.State
          break;
      }
    })
  }

  private changeDevice(device: WatchDevice) {
    DeviceManager.instance.resetState();
    enableDialog("")
    this.askDevices(device)
  }

  killDriverService() {
    this.socketService.askType(DriverSocketMsgType.MTKillService).then(() => {
      this.socketService.disconnect();
    }, (err) => {
      enableDialog(err, DialogType.notice, false)
    })
  }
}
